import React, { useMemo, useState } from 'react';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
import styled from 'styled-components';
import styles from '../../../utility/styles';
import Text from '../../shared/Text';

const geoUrl = '/data/countries-110m.json';

const MapWrapper = styled.div`
    width: 100%;
    max-width: 600px;
    margin: 0 auto;
    border-radius: 12px;
    overflow: hidden;
    background-color: ${styles.colors.darkBackground};
`

const TooltipBox = styled.div`
    position: fixed;
    pointer-events: none;
    padding: 4px 8px;
    background-color: ${styles.colors.background};
    border: 1px solid ${styles.colors.darkGray};
    border-radius: 4px;
    font-family: ${styles.fonts.geist};
    font-size: 11px;
    color: ${styles.colors.white};
    white-space: nowrap;
    z-index: 10;
`

const CountryList = styled.div`
    display: flex;
    flex-direction: column;
    margin-top: 16px;
`

const CountryRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 6px 0;
    border-bottom: 1px solid ${styles.colors.darkGray};
`

const CountryNameRow = styled.div`
    display: flex;
    align-items: center;
    flex: 1;
    gap: 8px;
`

const ACTIVE_COLOR = styles.colors.blue;
const INACTIVE_COLOR = '#232b3a';
const HOVER_COLOR = '#49BFFF';

/**
 * @param {{
 *   countries: { country_code: string, country_numeric: string, country_name: string, pct: number }[]
 * }} props
 */
export default function WorldMap({ countries = [] }) {
    const [tooltip, setTooltip] = useState(null)

    const activeCountries = useMemo(() => {
        const set = new Set()
        countries.forEach(c => set.add(c.country_numeric))
        return set
    }, [countries])

    if (!countries.length) return null

    return (
        <div>
            <MapWrapper>
                <ComposableMap
                    projection="geoNaturalEarth1"
                    projectionConfig={{ scale: 120, center: [0, 10] }}
                    style={{ width: '100%', height: 'auto' }}
                    width={600}
                    height={300}
                >
                    <Geographies geography={geoUrl}>
                        {({ geographies }) =>
                            geographies.map((geo) => {
                                const isActive = activeCountries.has(geo.id)
                                return (
                                    <Geography
                                        key={geo.rsmKey}
                                        geography={geo}
                                        fill={isActive ? ACTIVE_COLOR : INACTIVE_COLOR}
                                        stroke={styles.colors.background}
                                        strokeWidth={0.3}
                                        onMouseEnter={(e) => {
                                            const match = countries.find(c => c.country_numeric === geo.id)
                                            setTooltip({
                                                x: e.clientX,
                                                y: e.clientY,
                                                name: match?.country_name || geo.properties.name,
                                                pct: match?.pct
                                            })
                                        }}
                                        onMouseLeave={() => setTooltip(null)}
                                        style={{
                                            default: { outline: 'none' },
                                            hover: { outline: 'none', fill: isActive ? HOVER_COLOR : '#252d3d' },
                                            pressed: { outline: 'none' },
                                        }}
                                    />
                                )
                            })
                        }
                    </Geographies>
                </ComposableMap>
            </MapWrapper>
            {tooltip && (
                <TooltipBox style={{ left: tooltip.x + 10, top: tooltip.y - 30 }}>
                    {tooltip.name}{tooltip.pct != null ? ` - ${tooltip.pct}%` : ''}
                </TooltipBox>
            )}
            <CountryList>
                {countries.slice(0, 10).map((c) => (
                    <CountryRow key={c.country_code}>
                        <CountryNameRow>
                            <Text.Caption variant='bold' color={styles.colors.white} style={{ fontFamily: styles.fonts.geist }}>{c.country_name}</Text.Caption>
                        </CountryNameRow>
                        <Text.Caption color={styles.colors.lightGray} style={{ fontFamily: styles.fonts.geistMono }}>{c.pct}%</Text.Caption>
                    </CountryRow>
                ))}
            </CountryList>
        </div>
    )
}
